/*
 * Pure helpers for the Admin Center "User BCS Transform Certification Progress" table: status
 * presentation, sorting and cell formatting. Rows arrive unsorted from
 * GET /transformLearningGateway/api/v1/agency-certification-progress; the table sorts the
 * WHOLE dataset here and only then shows its first batch, so a sort never reorders just the
 * visible rows.
 */

export const CERTIFICATION_STATUS = Object.freeze({
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  NOT_CERTIFIED: 'not_certified',
  CERTIFIED: 'certified'
})

/*
 * `rank` drives the Status sort. Ascending goes from the furthest along to the least: Certified → Not Certified (100% without a record, pending the certification backfill)
 * → In Progress → Not Started.
 */
export const CERTIFICATION_STATUS_META = Object.freeze({
  [CERTIFICATION_STATUS.CERTIFIED]: { label: 'Certified', color: 'success', rank: 0 },
  [CERTIFICATION_STATUS.NOT_CERTIFIED]: { label: 'Not Certified', color: 'primary', rank: 1 },
  [CERTIFICATION_STATUS.IN_PROGRESS]: { label: 'In Progress', color: 'warning', rank: 2 },
  [CERTIFICATION_STATUS.NOT_STARTED]: { label: 'Not Started', color: 'gray', rank: 3 }
})

export const SORT_KEYS = Object.freeze({
  USER_NAME: 'userName',
  STATUS: 'status',
  PROGRESS: 'progressPercent',
  LAST_ACTIVITY: 'lastActivityAt'
})

export const SORT_DIRECTIONS = Object.freeze({ ASC: 'asc', DESC: 'desc' })

/* The table opens with the furthest along first: Progress 100% → 0%. */
export const DEFAULT_SORT = Object.freeze({ key: SORT_KEYS.PROGRESS, direction: SORT_DIRECTIONS.DESC })

function compareNames(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'en', { sensitivity: 'base' })
}

function statusRank(status) {
  return CERTIFICATION_STATUS_META[status]?.rank ?? Number.MAX_SAFE_INTEGER
}

function toMillis(value) {
  if (!value) return null
  const millis = new Date(value).getTime()
  return Number.isNaN(millis) ? null : millis
}

/*
 * The column comparison for one sort key, already signed by the direction. Last Activity is the
 * exception: users with no activity stay at the bottom in BOTH directions, so that part of the
 * comparison is never flipped.
 */
function compareByKey(a, b, key, sign) {
  switch (key) {
    case SORT_KEYS.USER_NAME:
      return sign * compareNames(a.userName, b.userName)
    case SORT_KEYS.STATUS:
      return sign * (statusRank(a.status) - statusRank(b.status))
    case SORT_KEYS.PROGRESS:
      return sign * ((a.progressPercent ?? 0) - (b.progressPercent ?? 0))
    case SORT_KEYS.LAST_ACTIVITY: {
      const aMillis = toMillis(a.lastActivityAt)
      const bMillis = toMillis(b.lastActivityAt)
      if (aMillis === null && bMillis === null) return 0
      if (aMillis === null) return 1
      if (bMillis === null) return -1
      return sign * (aMillis - bMillis)
    }
    default:
      return 0
  }
}

/**
 * Returns a new array sorted by one column; ties always fall back to User Name A→Z.
 * @param {Array<Object>} rows
 * @param {{key: string, direction: string}} [sort]
 */
export function sortCertificationRows(rows = [], sort = DEFAULT_SORT) {
  const sign = sort?.direction === SORT_DIRECTIONS.DESC ? -1 : 1
  return [...rows].sort((a, b) =>
    compareByKey(a, b, sort?.key, sign) || compareNames(a.userName, b.userName)
  )
}

/** "1 Video", "0 Videos", "2 Quizzes". */
export function formatCount(count, singular, plural) {
  const value = Number(count) || 0
  return `${value} ${value === 1 ? singular : plural}`
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Last Activity cell, in the browser's time zone: "—", "Today", "Yesterday" or "Aug 10, 2026"
 * (the date format Admin Center already uses).
 * @param {string|null} value - ISO date.
 * @param {Date} [now]
 */
export function formatLastActivity(value, now = new Date()) {
  const millis = toMillis(value)
  if (millis === null) return '—'
  const date = new Date(millis)
  // Rounded, not floored: a DST switch makes one calendar day 23 or 25 hours long.
  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS)
  if (daysAgo === 0) return 'Today'
  if (daysAgo === 1) return 'Yesterday'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
